import { isEmpty } from "@s-libs/micro-dash";
import { User } from "@/types/models/User/v3";
import { ILessonPlanNode } from "./useLessonPlanEditor/useLessonPlanEditor";

export const LESSON_PLAN_STORAGE_KEY = "lesson-plans";

export const DEFAULT_LESSON_PLAN = null;

/* removeStoredLesson() allows individual lessons to be removed from local storage */
export const removeStoredLesson = ({
  user,
  contentId,
}: {
  user: User | null | undefined;
  contentId: string;
}) => {
  const uniqueUserId = user?.unique_id || "";

  if (!uniqueUserId || !contentId) {
    removeStoredLessonPlans();
    return;
  }

  try {
    const savedLessons = localStorage.getItem(LESSON_PLAN_STORAGE_KEY);

    const lessons = savedLessons ? JSON.parse(savedLessons || "") : {};

    const value = JSON.stringify({
      ...lessons,
      [uniqueUserId]: {
        ...lessons[uniqueUserId],
        [contentId]: DEFAULT_LESSON_PLAN,
      },
    });

    localStorage.setItem(LESSON_PLAN_STORAGE_KEY, value);
  } catch (_e) {
    removeStoredLessonPlans();
  }
};

/*  removeStoredLessonPlans() removes all lesson plans from storage */
export const removeStoredLessonPlans = () => {
  localStorage.removeItem(LESSON_PLAN_STORAGE_KEY);
};

/* getStoredLesson() retrieves a lesson plan by content ID from local storage */
export const getStoredLesson = ({
  user,
  contentId,
}: {
  user: User | null | undefined;
  contentId: string;
}): ILessonPlanNode | null => {
  const uniqueUserId = user?.unique_id;

  if (!uniqueUserId) return DEFAULT_LESSON_PLAN;

  try {
    const lessonPlans = localStorage.getItem(LESSON_PLAN_STORAGE_KEY);

    const lessonPlan = lessonPlans
      ? JSON.parse(lessonPlans)?.[uniqueUserId]?.[contentId]
      : DEFAULT_LESSON_PLAN;

    return lessonPlan;
  } catch (_e) {
    removeStoredLessonPlans();
    return DEFAULT_LESSON_PLAN;
  }
};

/* updateStoredLesson updates a stored lesson plan by content ID */
export const updateStoredLesson = ({
  user,
  contentId,
  lessonPlan,
}: {
  user: User | null | undefined;
  contentId: string;
  lessonPlan: ILessonPlanNode | null | undefined;
}) => {
  const uniqueUserId = user?.unique_id;

  if (!uniqueUserId || !contentId || isEmpty(lessonPlan)) return;

  try {
    const savedLessons = localStorage.getItem(LESSON_PLAN_STORAGE_KEY);

    const lessons = savedLessons ? JSON.parse(savedLessons || "") : {};

    const value = JSON.stringify({
      ...lessons,
      [uniqueUserId]: {
        ...lessons[uniqueUserId],
        [contentId]: lessonPlan,
      },
    });

    localStorage.setItem(LESSON_PLAN_STORAGE_KEY, value);
  } catch (_e) {
    removeStoredLessonPlans();
  }
};
